// MOCK ONLY. Real surrogate ships server-side in Phase 2. Do not extend.
//
// Minimal surrogate used by the mock sampler to produce (mean, sigma) estimates
// at query points. The implementation is a stock textbook exponential-decay
// correlation with a small nugget for stability. We Cholesky-solve once per
// fit and reuse the factor for all queries in that fit.

type Matrix = number[][];

type TrainedSurrogate = {
  xs: number[][]; // normalised training inputs
  ys: number[]; // training outputs
  mean: number;
  std: number;
  L: Matrix; // lower-triangular Cholesky factor of (K + nugget I)
  alpha: number[]; // K^{-1} (y - mean)
  decay: number;
};

const NUGGET = 1e-6;
const DEFAULT_LENGTH = 0.3;

function safeGet(arr: number[], i: number): number {
  const v = arr[i];
  return v == null ? 0 : v;
}

function safeMat(m: Matrix, i: number, j: number): number {
  const row = m[i];
  if (!row) return 0;
  const v = row[j];
  return v == null ? 0 : v;
}

function correlation(a: number[], b: number[], decay: number): number {
  let d2 = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const dd = (safeGet(a, i) - safeGet(b, i)) / decay;
    d2 += dd * dd;
  }
  return Math.exp(-0.5 * d2);
}

function kernelVector(model: TrainedSurrogate, x: number[]): number[] {
  return model.xs.map((xi) => correlation(x, xi, model.decay));
}

function whitenedKernel(model: TrainedSurrogate, x: number[]): number[] {
  return forwardSub(model.L, kernelVector(model, x));
}

function cholesky(A: Matrix): Matrix {
  const n = A.length;
  const L: Matrix = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = safeMat(A, i, j);
      for (let k = 0; k < j; k++) sum -= safeMat(L, i, k) * safeMat(L, j, k);
      const Ljj = safeMat(L, j, j);
      const row = L[i];
      if (!row) continue;
      if (i === j) {
        row[j] = Math.sqrt(Math.max(sum, 1e-12));
      } else {
        row[j] = Ljj !== 0 ? sum / Ljj : 0;
      }
    }
  }
  return L;
}

function forwardSub(L: Matrix, b: number[]): number[] {
  const n = L.length;
  const y = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    let sum = safeGet(b, i);
    for (let k = 0; k < i; k++) sum -= safeMat(L, i, k) * safeGet(y, k);
    const d = safeMat(L, i, i);
    y[i] = d !== 0 ? sum / d : 0;
  }
  return y;
}

function backwardSub(L: Matrix, b: number[]): number[] {
  // Solves L^T x = b given lower-triangular L
  const n = L.length;
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = safeGet(b, i);
    for (let k = i + 1; k < n; k++) sum -= safeMat(L, k, i) * safeGet(x, k);
    const d = safeMat(L, i, i);
    x[i] = d !== 0 ? sum / d : 0;
  }
  return x;
}

export function fit(
  xs: number[][],
  ys: number[],
  decay = DEFAULT_LENGTH,
): TrainedSurrogate {
  const n = xs.length;
  if (n === 0) {
    return {
      xs: [],
      ys: [],
      mean: 0,
      std: 1,
      L: [],
      alpha: [],
      decay,
    };
  }

  const mean = ys.reduce((a, b) => a + b, 0) / n;
  let variance = 0;
  for (const v of ys) variance += (v - mean) * (v - mean);
  variance = n > 1 ? variance / (n - 1) : 1;
  const std = Math.sqrt(Math.max(variance, 1e-9));

  // Build correlation matrix K + nugget I.
  const K: Matrix = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    const rowI = K[i]!;
    rowI[i] = 1 + NUGGET;
    const xi = xs[i] ?? [];
    for (let j = 0; j < i; j++) {
      const xj = xs[j] ?? [];
      const c = correlation(xi, xj, decay);
      rowI[j] = c;
      const rowJ = K[j]!;
      rowJ[i] = c;
    }
  }

  const L = cholesky(K);
  const centred = ys.map((v) => (v - mean) / std);
  const z = forwardSub(L, centred);
  const alpha = backwardSub(L, z);

  return { xs, ys, mean, std, L, alpha, decay };
}

// Estimate (mean, sigma) at a single query point.
export function predict(
  model: TrainedSurrogate,
  x: number[],
): { mean: number; sigma: number } {
  const n = model.xs.length;
  if (n === 0) {
    // No data → prior: mean 0, wide sigma in normalised units.
    return { mean: 0, sigma: 1 };
  }
  const k: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const xi = model.xs[i] ?? [];
    k[i] = correlation(x, xi, model.decay);
  }
  let mu = 0;
  for (let i = 0; i < n; i++) mu += safeGet(k, i) * safeGet(model.alpha, i);
  mu = model.mean + model.std * mu;

  // sigma^2 = std^2 * (1 - k^T K^{-1} k). Solve K v = k via L.
  const z = forwardSub(model.L, k);
  let vTv = 0;
  for (const zi of z) vTv += zi * zi;
  const varNorm = Math.max(1 - vTv, 0);
  const sigma = model.std * Math.sqrt(varNorm + NUGGET);
  return { mean: mu, sigma };
}

export type DifferencePrediction = {
  /** Posterior mean of f(target) - f(source). */
  mean: number;
  /** Posterior standard deviation of the correlated difference. */
  sigma: number;
  targetVariance: number;
  sourceVariance: number;
  covariance: number;
  correlation: number;
};

/**
 * Latent posterior covariance between two GP queries. This deliberately omits
 * the numerical nugget used by `predict`: transport compares two values of the
 * same latent function, so a coincident source/target must have exactly zero
 * difference uncertainty.
 */
export function posteriorCovariance(
  model: TrainedSurrogate,
  a: number[],
  b: number[],
): number {
  const prior = correlation(a, b, model.decay);
  if (model.xs.length === 0) return model.std ** 2 * prior;
  const wa = whitenedKernel(model, a);
  const wb = whitenedKernel(model, b);
  let conditioned = prior;
  for (let index = 0; index < Math.min(wa.length, wb.length); index += 1) {
    conditioned -= safeGet(wa, index) * safeGet(wb, index);
  }
  return model.std ** 2 * conditioned;
}

/**
 * Joint-posterior uncertainty for f(target) - f(source). The covariance term
 * is essential: adding two marginal sigmas would substantially overstate a
 * short transport and would fail to collapse to zero when the points coincide.
 */
export function predictDifference(
  model: TrainedSurrogate,
  target: number[],
  source: number[],
): DifferencePrediction {
  const targetPrediction = predict(model, target);
  const sourcePrediction = predict(model, source);
  const targetVariance = Math.max(0, posteriorCovariance(model, target, target));
  const sourceVariance = Math.max(0, posteriorCovariance(model, source, source));
  const covariance = posteriorCovariance(model, target, source);
  const variance = Math.max(0, targetVariance + sourceVariance - 2 * covariance);
  const denominator = Math.sqrt(targetVariance * sourceVariance);
  return {
    mean: targetPrediction.mean - sourcePrediction.mean,
    sigma: Math.sqrt(variance),
    targetVariance,
    sourceVariance,
    covariance,
    correlation: denominator > 0 ? Math.max(-1, Math.min(1, covariance / denominator)) : 1,
  };
}

export type { TrainedSurrogate };
